import React from "react";
import { Navigate } from "react-router-dom";
import { useFeatureGating } from "@/contexts/FeatureGatingContext";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles } from "lucide-react";

interface FeatureProtectedRouteProps {
  children: React.ReactNode;
  featureType: string;
  redirectTo?: string;
}

export const FeatureProtectedRoute: React.FC<FeatureProtectedRouteProps> = ({
  children,
  featureType,
  redirectTo = "/settings?tab=billing",
}) => {
  const { canUseFeature, isLoading } = useFeatureGating();

  // Show loading state while feature gating data is loading
  // This prevents the premium blocker UI from flashing
  if (isLoading) {
    return <>{children}</>;
  }

  const featureAccess = canUseFeature(featureType as any);

  if (!featureAccess.allowed) {
    // Show upgrade page instead of just redirecting
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Premium Feature
          </h2>

          <p className="text-gray-600 mb-6">
            {featureAccess.message || "This feature requires an upgraded plan."}
          </p>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">
                Unlock AI Agents
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Get access to AI agents, voice calls, and chat features to
              automate your customer interactions.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => (window.location.href = redirectTo)}
              className="w-full"
              size="lg"
            >
              Upgrade Now
            </Button>

            <Button
              onClick={() => window.history.back()}
              variant="outline"
              className="w-full"
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
